import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../store/app/app.selectors';
import { catchError, filter, first, map, Observable, of, tap, timeout } from 'rxjs';
import { SetAppStateFromBusinessCertificateGuard, SetSessionExpired } from '../../store/app/app.actions';
import { BusinessStatus } from '../../_interfaces/business.interface';

/**
 * Helper to ensure the state is initialised before the guard logic runs.
 * Includes a timeout safety net to prevent infinite waiting if API calls fail.
 */
const ensureBusinessCertState = (store: Store) => {
  return store.select(AppSelectors.hasBusinessCert).pipe(
    tap((hasCert) => {
      if (hasCert === undefined) {
        store.dispatch(new SetAppStateFromBusinessCertificateGuard());
      }
    }),
    filter((hasCert) => hasCert !== undefined),
    first(),
    timeout(8000), // 8 second timeout to prevent hanging on failed API calls
    catchError(() => {
      // If the API call times out or fails, trigger session expired flow
      store.dispatch(new SetSessionExpired(true));
      return of(false);
    }),
  );
};

// 1. BusinessCertGuard (Protects routes that NEED a certificate)
export const canActivateBusinessCert: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const store = inject(Store);
  const router = inject(Router);

  return ensureBusinessCertState(store).pipe(
    map((hasCert) => {
      if (!hasCert) {
        return router.parseUrl('sign-up/business-certificate');
      }
      return true;
    }),
  );
};

// 2. HasBusinessCertGuard (Prevents users WITH a cert from going back to sign-up)
export const canActivateHasBusinessCert: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const store = inject(Store);
  const router = inject(Router);

  return ensureBusinessCertState(store).pipe(
    map((hasCert) => {
      if (hasCert) {
        return router.parseUrl('/dashboard');
      }
      return true;
    }),
  );
};

// 3. HasBusinessCertErrorGuard (Allows entry only if status is Error or Rejected)
export const canActivateHasBusinessCertError: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const store = inject(Store);
  const router = inject(Router);

  return store.select(AppSelectors.businessDetails).pipe(
    tap((details) => {
      if (details?.cert_file === undefined) {
        store.dispatch(new SetAppStateFromBusinessCertificateGuard());
      }
    }),
    filter((details) => details?.cert_file !== undefined),
    first(),
    map((details) => {
      const isRetryable =
        details?.cert_file && (details?.status === BusinessStatus.Error || details?.status === BusinessStatus.Rejected);

      return isRetryable ? true : router.parseUrl('/dashboard');
    }),
    timeout(8000), // 8 second timeout to prevent hanging on failed API calls
    catchError(() => {
      // If the API call times out or fails, trigger session expired flow
      store.dispatch(new SetSessionExpired(true));
      return of(router.parseUrl('/dashboard'));
    }),
  );
};
